"use client"

import { createContext, useContext, useState, type ReactNode } from "react"

export interface Company {
  id: string
  name: string
  industry?: string
  size?: string
  location?: string
  website?: string
  description?: string
  createdAt: string
  updatedAt: string
}

export interface CompanyContextType {
  companies: Company[]
  addCompany: (company: Omit<Company, "id" | "createdAt" | "updatedAt">) => Company
  updateCompany: (id: string, updates: Partial<Company>) => void
  getCompanyById: (id: string) => Company | undefined
  searchCompanies: (query: string) => Company[]
}

export const CompanyContext = createContext<CompanyContextType | undefined>(undefined)

export function useCompany() {
  const context = useContext(CompanyContext)
  if (!context) {
    throw new Error("useCompany must be used within a CompanyProvider")
  }
  return context
}

export function CompanyProvider({ children }: { children: ReactNode }) {
  const [companies, setCompanies] = useState<Company[]>([
    {
      id: "1",
      name: "TechCorp",
      industry: "Technology",
      size: "1000-5000",
      location: "San Francisco, CA",
      website: "https://techcorp.com",
      description: "Leading technology company specializing in cloud infrastructure",
      createdAt: "2024-01-01T00:00:00Z",
      updatedAt: "2024-01-01T00:00:00Z",
    },
    {
      id: "2",
      name: "StartupIO",
      industry: "Fintech",
      size: "50-200",
      location: "New York, NY",
      website: "https://startup.io",
      description: "Fast-growing fintech startup revolutionizing payments",
      createdAt: "2024-01-02T00:00:00Z",
      updatedAt: "2024-01-02T00:00:00Z",
    },
    {
      id: "3",
      name: "InnovateAI",
      industry: "Artificial Intelligence",
      size: "200-500",
      location: "Austin, TX",
      website: "https://innovateai.com",
      description: "AI infrastructure platform for enterprise applications",
      createdAt: "2024-01-03T00:00:00Z",
      updatedAt: "2024-01-03T00:00:00Z",
    },
  ])

  const addCompany = (companyData: Omit<Company, "id" | "createdAt" | "updatedAt">): Company => {
    const now = new Date().toISOString()
    const newCompany: Company = {
      ...companyData,
      id: Date.now().toString(),
      createdAt: now,
      updatedAt: now,
    }
    setCompanies((prev) => [...prev, newCompany])
    return newCompany
  }

  const updateCompany = (id: string, updates: Partial<Company>) => {
    setCompanies((prev) =>
      prev.map((company) =>
        company.id === id ? { ...company, ...updates, updatedAt: new Date().toISOString() } : company,
      ),
    )
  }

  const getCompanyById = (id: string): Company | undefined => {
    return companies.find((company) => company.id === id)
  }

  const searchCompanies = (query: string): Company[] => {
    if (!query.trim()) return companies
    const lowercaseQuery = query.toLowerCase()
    return companies.filter(
      (company) =>
        company.name.toLowerCase().includes(lowercaseQuery) ||
        company.industry?.toLowerCase().includes(lowercaseQuery) ||
        company.location?.toLowerCase().includes(lowercaseQuery),
    )
  }

  return (
    <CompanyContext.Provider
      value={{
        companies,
        addCompany,
        updateCompany,
        getCompanyById,
        searchCompanies,
      }}
    >
      {children}
    </CompanyContext.Provider>
  )
}
